import type { useTranslate } from "@similarweb/i18n-provider";
import type { ProWebsiteModelsConversionRatesDtoCategoriesConversionRatesOverview } from "@similarweb/pro-api-client";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { ChangeCell, ProgressBarWithValueCell, TextCell } from "@similarweb/pro-logic-components";
import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";
import { LegendBullet } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { CONVERSION_RATE_TRANSLATION_KEYS, conversionRateFormatters } from "../../consts";
import { comparisonRowAutomationId } from "../automationIds";
import type { IComparisonRow } from "../buildComparisonRows";

type Translate = ReturnType<typeof useTranslate>["translate"];
type Entry = ProWebsiteModelsConversionRatesDtoCategoriesConversionRatesOverview;

const NA_DASH = "—";
const COLUMN_MIN_WIDTH = 200;

const renderIndustry = (row: IComparisonRow<Entry>, automationPrefix: string) => (
    <Flex
        alignItems="center"
        gap="8px"
        data-automation={comparisonRowAutomationId(automationPrefix, row.id, "industry")}
    >
        <LegendBullet
            labelColor={row.color}
            text={
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[400]}
                >
                    {row.name}
                </Text>
            }
        />
    </Flex>
);

const renderConvertedVisits = (row: IComparisonRow<Entry>, automationPrefix: string) => {
    const entry = row.entry;
    const automationId = comparisonRowAutomationId(automationPrefix, row.id, "converted-visits");
    if (!entry) {
        return <TextCell dataAutomation={automationId}>{NA_DASH}</TextCell>;
    }
    return (
        <ProgressBarWithValueCell
            share={entry.normalizedConvertedVisits}
            valueMinWidth="64px"
            color={row.color as StrictColor}
            value={conversionRateFormatters.convertedVisits(entry.convertedVisits)}
        />
    );
};

const renderChange = (row: IComparisonRow<Entry>, automationPrefix: string) => {
    const change = row.entry?.convertedVisitsChange;
    return (
        <Flex
            justifyContent="flex-end"
            data-automation={comparisonRowAutomationId(automationPrefix, row.id, "change")}
        >
            <ChangeCell
                value={change ?? undefined}
                formattedValue={change == null ? NA_DASH : conversionRateFormatters.visitsChange(change)}
                noDataSign={NA_DASH}
            />
        </Flex>
    );
};

const renderAvgRate = (row: IComparisonRow<Entry>, automationPrefix: string) => {
    const avg = row.entry?.avgConversionRate;
    return (
        <Flex
            justifyContent="flex-end"
            data-automation={comparisonRowAutomationId(automationPrefix, row.id, "avg-rate")}
        >
            <TextCell>{avg == null ? NA_DASH : conversionRateFormatters.avgConversionRate(avg)}</TextCell>
        </Flex>
    );
};

export const getComparisonOverviewColumns = (
    translate: Translate,
    automationPrefix: string,
): ColumnsType<IComparisonRow<Entry>> => [
    {
        key: "industry",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonColIndustry),
        minWidth: COLUMN_MIN_WIDTH,
        render: (_, row) => renderIndustry(row, automationPrefix),
    },
    {
        key: "convertedVisits",
        align: "right",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonColConvertedVisits),
        minWidth: COLUMN_MIN_WIDTH,
        render: (_, row) => renderConvertedVisits(row, automationPrefix),
    },
    {
        key: "change",
        align: "right",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonColChange),
        minWidth: COLUMN_MIN_WIDTH,
        render: (_, row) => renderChange(row, automationPrefix),
    },
    {
        key: "avgRate",
        align: "right",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonColAvgRate),
        minWidth: COLUMN_MIN_WIDTH,
        render: (_, row) => renderAvgRate(row, automationPrefix),
    },
];
